import React from 'react';
import Box from '@mui/material/Box';
import { useCountUp } from '../../hooks/useCountUp';

/**
 * Rolls the numeric part of a preformatted hero string up on load, preserving
 * its prefix (£, -£), thousands separators and decimal places. Falls back to
 * the raw string when there is no number to animate (e.g. "—", "N/A").
 */
const AnimatedHeroValue: React.FC<{ value: string }> = ({ value }) => {
  const match = value.match(/-?[\d,]*\.?\d+/);
  const numeric = match ? parseFloat(match[0].replace(/,/g, '')) : NaN;
  const decimals = match && match[0].includes('.') ? (match[0].split('.')[1]?.length ?? 0) : 0;
  const animated = useCountUp(Number.isFinite(numeric) ? numeric : 0);

  if (!match || !Number.isFinite(numeric)) return <>{value}</>;

  const formatted = animated.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return <>{value.replace(match[0], formatted)}</>;
};

/**
 * Confident-shell hero band (owner-approved direction, 2026-08-05).
 *
 * Brings the login screen's authority onto money screens: deep forest-black
 * gradient, serif display title, and THE one number that matters rendered
 * large in mint. Colours come exclusively from the --band-* tokens.
 *
 * MOBILE HEIGHT (owner ask, 2026-08-07): on a 390px phone the band used to
 * wrap into THREE stacked rows — title, actions, hero — costing 207px, a
 * quarter of the viewport. Below `sm` the hero number now shares row one with
 * the title (`order`), actions drop to their own full-width row, and padding
 * and gaps tighten. It also carries `collapsible-chrome`, so useChromeCollapse
 * folds it away entirely while the user scrolls a list.
 */
export interface PageHeroBandProps {
  /** Small uppercase context line, e.g. "Finance · This month" */
  eyebrow?: string;
  /** Page title (serif display) */
  title: string;
  /** Label above the hero value, e.g. "Net profit" */
  heroLabel?: string;
  /** Preformatted hero value, e.g. "£4,400" */
  heroValue?: string;
  /** Optional right-side extra content (replaces hero number if given) */
  right?: React.ReactNode;
  /** Action buttons row, rendered under the hero value / right content */
  actions?: React.ReactNode;
  'data-testid'?: string;
}

const PageHeroBand: React.FC<PageHeroBandProps> = ({
  eyebrow,
  title,
  heroLabel,
  heroValue,
  right,
  actions,
  'data-testid': testId = 'page-hero-band',
}) => (
  <Box
    data-testid={testId}
    className="collapsible-chrome"
    sx={{
      background: 'var(--band-bg)',
      color: 'var(--band-text)',
      borderRadius: { xs: '14px', sm: '16px' },
      padding: {
        xs: '14px 16px',
        sm: 'clamp(18px, 2.5vw, 28px) clamp(20px, 3vw, 32px)',
      },
      // Phone: an explicit 2-column grid — title left, hero right, actions
      // spanning row 2. Flex-wrap was pushing all three onto their own rows.
      display: { xs: 'grid', sm: 'flex' },
      gridTemplateColumns: { xs: 'minmax(0,1fr) auto', sm: 'none' },
      alignItems: { xs: 'center', sm: 'flex-end' },
      justifyContent: { sm: 'space-between' },
      flexWrap: { sm: 'wrap' },
      columnGap: { xs: '12px', sm: '16px' },
      rowGap: { xs: '10px', sm: '16px' },
      marginBottom: { xs: '14px', sm: '20px' },
    }}
  >
    <Box
      sx={{
        minWidth: 0,
        order: 1,
        gridColumn: { xs: '1', sm: 'auto' },
        gridRow: { xs: '1', sm: 'auto' },
      }}
    >
      {eyebrow && (
        <Box
          sx={{
            fontSize: { xs: 11, sm: 12 },
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'var(--band-eyebrow)',
          }}
        >
          {eyebrow}
        </Box>
      )}
      <Box
        component="h1"
        sx={{
          fontSize: 'clamp(20px, 3vw, 32px)',
          fontWeight: 800,
          fontFamily: 'Georgia, "Times New Roman", serif',
          lineHeight: 1.15,
          margin: '2px 0 0',
          color: 'var(--band-text)',
        }}
      >
        {title}
      </Box>
    </Box>
    {actions && (
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          flexWrap: 'wrap',
          // Phone: actions take their own row BELOW title + hero.
          order: { xs: 3, sm: 2 },
          gridColumn: { xs: '1 / -1', sm: 'auto' },
          gridRow: { xs: '2', sm: 'auto' },
        }}
      >
        {actions}
      </Box>
    )}
    {(right != null || heroValue != null) && (
      <Box
        sx={{
          textAlign: 'right',
          order: { xs: 2, sm: 3 },
          flexShrink: 0,
          gridColumn: { xs: '2', sm: 'auto' },
          gridRow: { xs: '1', sm: 'auto' },
        }}
      >
        {right ?? (
          <>
            {heroLabel && (
              <Box
                sx={{
                  fontSize: { xs: 10, sm: 12 },
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  color: 'var(--band-eyebrow)',
                }}
              >
                {heroLabel}
              </Box>
            )}
            <Box
              sx={{
                fontSize: 'clamp(24px, 4vw, 44px)',
                fontWeight: 800,
                fontVariantNumeric: 'tabular-nums',
                letterSpacing: '-0.01em',
                lineHeight: 1.1,
                color: 'var(--band-accent)',
              }}
            >
              <AnimatedHeroValue value={heroValue} />
            </Box>
          </>
        )}
      </Box>
    )}
  </Box>
);

export default PageHeroBand;
